import { assign, EventObject, MetaObject, send } from 'xstate';

import config from '../../config';

import {
  installTracking,
  sendTrackEvents,
  trackGameClick,
  trackMenuClick,
  trackPromoClick,
  trackOpenSearch,
  trackSearchResult,
  trackViewChange,
  trackPartialEvent,
  trackPendingAccountEvent,
  saveSiteSpecificTrackingInfo,
  saveInitialTrackingData,
  updateUserAccountNumber,
  updateUserBalance,
  updateUserCurrency,
  updateLoginStatus,
  trackTrebuchetPartialEvent,
  updateGameTrackingStorage,
  resetGameTrackingStorage,
  trackSuggestedGameClick,
} from '../../model/tracking';
import models from '../../model';
import { getCanonicalUrl } from '../../model/seo';
import Cookies from '../../ports-frontend/Cookie';
import Scroll from '../../ports-frontend/Scroll';
import Window from '../../ports-frontend/Window';
import Location from '../../ports-frontend/Location';
import Document from '../../ports-frontend/Document';
import MessageBus from '../../ports-frontend/MessageBus';
import { UpdatePersonalizedBannersEvent } from './types';
import {
  CRM_COOKIES_CONSENT_KEY,
  GAMING_SEGMENTS_COOKIE_NAME,
  HOMEPAGE_BASE_PATH,
} from './constants';
import {
  GameLaunchEvent,
  LogicContext,
  SearchClickEvent,
  SearchSubheaderClickEvent,
  UpdateGamesEvent,
  UpdateJackpotsEvent,
  UpdateRecentlyPlayedEvent,
  UpdateTablesEvent,
  NavigationClickEvent,
  PromoClickEvent,
  AuthTokenEvent,
  SegmentsEvent,
} from './types';
import {
  managePhrases,
  trackingDisabled,
  trackingDelayed,
  getCallbackMode,
  getPreviousPageArray,
  getPromotionsPath,
  omitKeys,
} from './utils';
import {
  controllersEnum,
  removeSpecificControllers,
  controllersComponentsMapper,
} from '../controllers';

const actions = {
  saveScrollPosition: () => {
    Scroll.saveScrollPosition();
  },
  scrollTop: (context: LogicContext, event: any) => {
    if (event?.data?.noScroll) {
      return;
    }
    Scroll.scrollTop();
  },
  closeApplication: () => {
    Window.postMessage('wh.xsell.command.gaming-lobby.close', '*');
  },
  handleOpenInIframe: () => {
    config.features.iFrame.mode === 'disabled' && Window.handleOpenInIframe();
  },

  sendTrackEvents: () => {
    sendTrackEvents();
  },
  // TRACKING
  installTracking: (context: LogicContext) => {
    if (trackingDisabled(context) || trackingDelayed()) {
      return;
    }
    installTracking();
  },
  updateGameLaunchedTracking: (context: LogicContext) =>
    updateGameTrackingStorage(context.gameToLaunch),
  resetGameLaunchedTracking: () => resetGameTrackingStorage(),
  trackGameClick: (context: LogicContext, event: GameLaunchEvent) => {
    if (trackingDisabled(context)) {
      return;
    }
    trackGameClick({
      gameParams: event.payload,
    });
  },
  trackSuggestedGameClick: (context: LogicContext, event: GameLaunchEvent) => {
    if (trackingDisabled(context) || !context.gameToLaunch) {
      return;
    }

    trackSuggestedGameClick({
      game: context.gameToLaunch,
    });
  },
  trackPromoClick: (context: LogicContext, event: PromoClickEvent) => {
    if (
      trackingDisabled(context) ||
      !event.payload.isBanner ||
      event.payload.isPersonalizedBanner
    ) {
      return;
    }
    const mappedUrl =
      event.payload.url ||
      `${config.urls.app}${config.base_path}${event.payload.path}`;
    trackPromoClick(mappedUrl);
  },
  trackPageChange: (context: LogicContext) => {
    if (trackingDisabled(context)) {
      return;
    }

    trackViewChange();
  },

  trackSearchResult: (context: LogicContext) => {
    if (trackingDisabled(context)) {
      return;
    }

    trackSearchResult({
      search: context.search,
    });
  },

  trackOpenSearch: (context: LogicContext) => {
    if (trackingDisabled(context)) {
      return;
    }
    trackOpenSearch();
  },

  trackHamburgerClick: (context: LogicContext) => {
    if (trackingDisabled(context)) {
      return;
    }

    trackMenuClick({
      title: 'Menu',
      actionTitle: 'Burger Menu',
    });
  },

  trackErrorItemClick: (context: LogicContext, event: any) => {
    if (trackingDisabled(context)) {
      return;
    }

    trackMenuClick({
      title: event.payload.title || '',
      actionTitle: 'Error',
    });
  },

  trackMainMenuItemClick: (context: LogicContext, event: any) => {
    if (trackingDisabled(context)) {
      return;
    }
    const { sectionTitle, itemTitle, containerTitle } = event.payload;
    const actionTitle =
      containerTitle || (sectionTitle === 'Games' ? 'Main Menu' : sectionTitle);

    trackMenuClick({
      title: itemTitle,
      actionTitle,
    });
  },

  trackSideItemClick: (context: LogicContext, event: any) => {
    if (trackingDisabled(context)) {
      return;
    }
    const { itemTitle } = event.payload;

    trackMenuClick({
      title: itemTitle,
    });
  },

  trackBottomNavigationClick: (
    context: LogicContext,
    event: { payload: { itemTitle: string } }
  ) => {
    if (trackingDisabled(context)) {
      return;
    }
    const { itemTitle } = event.payload;

    trackMenuClick({
      title: itemTitle,
      actionTitle: 'Bottom Nav',
    });
  },

  trackMenuItemClick: (
    context: LogicContext,
    event: { payload: { pageId: string; itemTitle: string } }
  ) => {
    if (trackingDisabled(context)) {
      return;
    }

    const { itemTitle } = event.payload;

    trackMenuClick({
      title: itemTitle,
    });
  },

  trackTrebuchetPartialEvent: (context: LogicContext, event: any) => {
    const { event: name, payload } = event.payload;
    trackTrebuchetPartialEvent(name, payload);
  },

  trackPartialEvent: assign((context: LogicContext, event: any) => {
    if (trackingDisabled(context)) {
      return context;
    }
    const { event: name, payload } = event.payload;

    // prevent MTT / Kraken from overwriting data controlled by Gaming
    const payloadPartialEvent = omitKeys(
      [
        'LandingDocumentLocation',
        'PageUrl',
        'PageTitle',
        'SiteLanguage',
        'SiteSection',
      ],
      payload
    );
    const pendingAccountEvent = trackPartialEvent({
      name,
      payloadPartialEvent,
      isAccountInfoAvailable: context.isAccountInfoAvailable,
    });
    if (!pendingAccountEvent) {
      return context;
    }

    return {
      ...context,
      isAccountInfoAvailable: false,
      pendingAccountEvents: [
        ...context.pendingAccountEvents,
        pendingAccountEvent,
      ],
    };
  }),

  saveInfoAccountInfo: assign((context: LogicContext) => {
    return {
      ...context,
      isAccountInfoAvailable: true,
    };
  }),

  trackPendingAccountEvents: assign((context: LogicContext) => {
    const { pendingAccountEvents = [], segments } = context;
    pendingAccountEvents.forEach((event: any) => {
      trackPendingAccountEvent({
        ...event,
        segments,
      });
    });
    return {
      ...context,
      pendingAccountEvents: [],
    };
  }),

  logEvent: (context: LogicContext, event: any) => {
    console.log('EVENT ', event);
  },

  saveInitialTrackingData: (context: LogicContext) => {
    if (trackingDisabled(context)) {
      return;
    }

    saveInitialTrackingData();
  },

  updateSiteTrackingStorageInfo: (context: LogicContext) => {
    if (trackingDisabled(context)) {
      return;
    }

    saveSiteSpecificTrackingInfo(
      context.trackingData,
      config.version
        ? context.currentPagePath.replace(`${config.version}`, '')
        : context.currentPagePath,
      context.segments
    );
  },

  updateUserTrackingInfo: (context: LogicContext) => {
    updateLoginStatus(context.user.loggedIn);
    updateUserAccountNumber(context.user.userAccountNumber);
    updateUserBalance(context.user.balance);
    updateUserCurrency(context.user.currency);
  },

  updateSeo: async (context: LogicContext, event: { pageSlug?: string }) => {
    const seo = context.seo?.page;

    if (!seo) return;

    const canonicalUrl = await getCanonicalUrl(seo.canonicalLinks[0]);
    const appUrl = !process.env.PUBLIC_URL
      ? config.urls.app
      : config.urls.app && new URL(config.urls.app).origin;

    Document.setTitle(seo.title);
    Document.setMetaDescription(seo.description);
    const noIndex = config.disallowIndexing?.enabled ? true : seo.noIndex;
    Document.manageRobotsTag(noIndex, seo.noTranslate);
    Document.setCanonicalTag(canonicalUrl);
    Document.setSchemaMarkUp(seo.structuredData);
    Document.removeHreflangTags(seo.canonicalLinks[0]);

    Document.OpenGraph.setDescription(seo.description);
    Document.OpenGraph.setTitle(seo.title);
    Document.OpenGraph.setURL(canonicalUrl);
    Document.OpenGraph.setType('website');
    Document.OpenGraph.setImage(
      seo.image ||
        `${appUrl}${process.env.PUBLIC_URL}/vegas-favicon-transparent.png`
    );

    Document.TwitterCards.setDescription(seo.description);
    Document.TwitterCards.setTitle(seo.title);
    Document.TwitterCards.setURL(canonicalUrl);
    Document.TwitterCards.setType('summary');
    Document.TwitterCards.setImage(
      seo.image ||
        `${appUrl}${process.env.PUBLIC_URL}/vegas-favicon-transparent.png`
    );
  },

  removeGameLaunchController: assign((context: LogicContext) => {
    return {
      ...context,
      controllersData: removeSpecificControllers(
        context.controllersData,
        controllersComponentsMapper[controllersEnum.GAME_LAUNCHER]
      ),
    };
  }),

  openUsabilla: () => {
    Window.openUsabilla();
  },

  openAccountHelp: function() {
    const helpPageApiPath = '/app/';
    const helpPageBaseUrl = {
      'en-gb': 'https://williamhill-lang.custhelp.com',
      'en-ca': 'https://williamhill-lang.custhelp.com',
      'it-it': 'https://williamhill-it.custhelp.com',
      'es-es': 'https://williamhill-es.custhelp.com',
    };
    Window.openSite(
      (helpPageBaseUrl[config.defaultLocale] || helpPageBaseUrl['en-gb']) +
        helpPageApiPath,
      '_blank'
    );
  },

  setBodyNonScrollable: () => {
    Document.setBodyNonScrollable();
  },

  setBodyScrollable: () => {
    Document.setBodyScrollable();
  },

  sendGameModalCloseToGameFrame: () => {
    MessageBus.publishEvent({
      topic: 'wh.trebuchet.command.ui.game.close',
    });
  },

  //Location
  updateUrl: (context: LogicContext) => {
    const pathToUpdate = context.currentPagePath;
    Location.set(pathToUpdate);
  },

  // Header Product Navigation
  trackRedirectToProduct: (
    context: LogicContext,
    event: {
      type: string;
      data: {
        label: string;
        link: {
          url: string;
        };
      };
    }
  ) => {
    if (event.type === 'VIEW_HEADER_PRODUCT_EVENT_CALLED') {
      if (!trackingDisabled(context)) {
        trackMenuClick({
          title: event.data?.label,
          actionTitle: 'Top Nav',
        });
      }
    }
  },

  initiateRedirectWithDelay: send(
    (
      context: LogicContext,
      event: {
        type: string;
        data: {
          label: string;
          link: {
            url: string;
          };
        };
      }
    ) => ({
      type: 'REDIRECT_TO_PRODUCT',
      data: event.data,
    }),
    { delay: 300 }
  ),

  redirectToProductPage: (
    context: LogicContext,
    event: {
      type: string;
      data: {
        label: string;
        link: {
          url: string;
        };
      };
    }
  ) => {
    if (event.type === 'REDIRECT_TO_PRODUCT' && event.data?.link?.url) {
      Window.redirectToUrl(event.data.link.url);
    }
  },

  openPromotionsPage: (
    context: LogicContext,
    event: { payload: { path: string } }
  ) => {
    const path = event.payload.path;
    path && Location.set(path);
  },

  updateBalance: () => {
    MessageBus.publishEvent({ topic: 'refreshBalance' });
  },

  updatePromotionsPath: (
    context: LogicContext,
    event: { pageSlug?: string }
  ) => {
    const path = getPromotionsPath(event.pageSlug);

    saveSiteSpecificTrackingInfo(
      context.trackingData,
      config.version ? path.replace(`${config.version}`, '') : path,
      context.segments
    );

    const pathToUpdate = path;
    pathToUpdate && Location.set(pathToUpdate);
  },

  resetSearchAttempts: assign<LogicContext>({
    search: (context: LogicContext) => ({
      ...context.search,
      attempts: 0,
    }),
  }),

  updateSearchAttempts: assign<LogicContext>({
    search: (context: LogicContext) => ({
      ...context.search,
      attempts: context.search.attempts ? context.search.attempts + 1 : 1,
    }),
  }),

  saveSearchInputType: assign<LogicContext>({
    search: (context: LogicContext, event: EventObject, meta: MetaObject) => ({
      ...context.search,
      sourceType: meta.action.data.sourceType,
    }),
  }),

  handleSubnavigationScrollReset: assign(
    (context: LogicContext, event: NavigationClickEvent) => {
      const path: string = event?.payload?.path || '';
      return {
        ...context,
        subnavigationScrollPosition:
          path.lastIndexOf('/') === path.indexOf('/') ? 0 : undefined,
      };
    }
  ),

  sendAlgoliaAnalytics: async (
    context: LogicContext,
    event: GameLaunchEvent
  ) => {
    const { default: searchInsights } = await import(
      /* webpackChunkName: "search-insights" */ 'search-insights'
    );
    const { userToken, queryID } = context.search;
    const { gameId, gamePosition } = event.payload;
    const basicObject = {
      userToken,
      eventName: `searched_game_launch`,
      index: config.algolia.config.index,
      queryID,
      objectIDs: [gameId],
    };

    searchInsights.clickedObjectIDsAfterSearch({
      ...basicObject,
      positions: [gamePosition],
    });

    searchInsights.convertedObjectIDsAfterSearch(basicObject);
  },

  // CATEGORIES
  resetPageAction: assign((context: LogicContext, event: any) => {
    if (context.changePageAction === 'BACK') {
      return {
        ...context,
        changePageAction: '',
        restoreScroll: true,
      };
    }
    return {
      ...context,
    };
  }),

  clearScrollRestore: assign(() => ({
    restoreScroll: false,
  })),

  restoreScrollPosition: () => {
    Scroll.restoreScrollPosition();
  },

  savePreviousUser: assign((context: LogicContext) => {
    return {
      context,
      previousUserAccountNumber: context.user.userAccountNumber,
    };
  }),

  updateCurrentPagePath: assign((context: LogicContext, event: any) => {
    let pagePath = event.payload?.path || event.data?.path;

    if (event.payload?.pageSlug) {
      pagePath = getPromotionsPath(event.payload?.pageSlug);
    }

    return pagePath
      ? {
          ...context,
          mainLayoutPath: pagePath,
          currentPagePath: pagePath,
        }
      : context;
  }),

  updatePrevPageArray: assign((context: LogicContext, event: any) => {
    const pagePath = event.payload?.path || event.data?.path;

    return {
      ...context,
      previousPageArray:
        context.currentPagePath !== pagePath
          ? getPreviousPageArray({
              pageParams: {
                pagePath: context.currentPagePath,
                isInSidebar: !!context.isSidebarInLayout,
                trackingData: context.trackingData?.page,
              },
              previousPageArray: context.previousPageArray,
              arraySizeLimit: 10,
            })
          : context.previousPageArray,
    };
  }),

  removeLastPagePath: assign((context: LogicContext) => {
    const basePath = config.base_path || HOMEPAGE_BASE_PATH;

    if (context.previousPageArray) {
      const reversedPageArray = [...context.previousPageArray]
        .slice()
        .reverse();
      return {
        ...context,
        currentPagePath:
          reversedPageArray.find(
            page => page.pagePath !== context.currentPagePath
          )?.pagePath || basePath,
        previousPageArray: context.previousPageArray.slice(0, -1),
      };
    }

    return {
      ...context,
      currentPagePath: basePath,
    };
  }),

  logger: (context: LogicContext, event: any) => {
    console.log({ context, event });
  },
  _logger_event: (context: LogicContext, event: any) => {
    console.log(JSON.stringify(event, null, 2));
  },

  setHeaderInitialized: assign((context: LogicContext) => ({
    ...context,
    headerInitialized: true,
  })),

  // SEARCH
  savePhrase: async (context: LogicContext) => {
    if (!context.user.loggedIn) return;

    let phrases = context.recentPhrases;
    if ((await Cookies.get('recentPhrases')) && phrases.length) {
      const localPhrases = JSON.parse(await Cookies.get('recentPhrases'));
      phrases = managePhrases(phrases, localPhrases, 5);
    }
    await Cookies.set('recentPhrases', phrases);
  },

  prepareSearchInputConfig: assign((context: LogicContext) => {
    const { deviceInfo } = context;
    const isIOS = deviceInfo.nativeDeviceOs === 'ios';
    const isRenderedInIFrame = config.features.iFrame.mode === 'enabled';

    return {
      ...context,
      inputConfig: {
        isIOS,
        isRenderedInIFrame,
      },
    };
  }),

  clearSearch: assign((context: LogicContext) => {
    const phrase = '';
    const subheaderItems: Array<{
      title: string;
      type: string;
      state: 'active' | 'inactive';
    }> = [];

    return {
      ...context,
      search: {
        ...context.search,
        searchItems: [],
        searchPhrase: phrase,
        subheaderItems: subheaderItems,
        itemsNumber: null,
      },
    };
  }),

  showGamesOfMatchingType: assign(
    (context: LogicContext, event: SearchSubheaderClickEvent) => {
      const currentType: string = event.payload.type;
      const itemsNumber = context.search.searchItems.filter(
        i => i.type === currentType
      ).length;

      return {
        ...context,
        search: {
          ...context.search,
          currentType,
          itemsNumber,
        },
      };
    }
  ),

  updateCurrentPhrase: assign(
    (context: LogicContext, event: SearchClickEvent) => {
      const phrase = event.payload.value;
      return {
        ...context,
        search: {
          ...context.search,
          searchPhrase: phrase,
          itemsNumber: null,
        },
      };
    }
  ),

  updatePhrases: assign((context: LogicContext) => {
    const phrase = context.search?.searchPhrase;
    if (!phrase) return context;
    let { recentPhrases, phrasePendingAdditionToRecentPhrases } = context;
    let phraseToAdd = phrasePendingAdditionToRecentPhrases;

    if (!phrase || phrase.length < 3) {
      return context;
    }
    if (!context.user.loggedIn) {
      return {
        ...context,
        phrasePendingAdditionToRecentPhrases: phrase,
      };
    }

    if (phraseToAdd) {
      recentPhrases = managePhrases(recentPhrases, phraseToAdd, 5);
      phraseToAdd = '';
    }

    return {
      ...context,
      recentPhrases: managePhrases(recentPhrases, phrase, 5),
      phrasePendingAdditionToRecentPhrases: phraseToAdd,
    };
  }),

  setCookieBannerCookie: async (context: LogicContext) => {
    await Cookies.set(CRM_COOKIES_CONSENT_KEY, '', {
      expires: 3600 * 24 * 365 * 10,
    });
  },

  // GAMES
  setSuggestedGameUrl: assign((context: LogicContext, event: any) => {
    if (context.gameToLaunch) {
      return {
        ...context,
        gameToLaunch: {
          ...context.gameToLaunch,
          triggeredByUser: true,
          launchUrl: event.payload.url,
          modeLaunchUrl: event.payload.url,
        },
      };
    }

    return context;
  }),

  updateGames: assign((context: LogicContext, event: UpdateGamesEvent) => {
    const { games } = event.data;

    return {
      ...context,
      games: {
        ...context.games,
        ...games,
      },
    };
  }),

  updateJackpots: assign(
    (context: LogicContext, event: UpdateJackpotsEvent) => {
      const { jackpots } = event.data;

      return {
        ...context,
        currentJackpots: {
          ...context.currentJackpots,
          ...jackpots,
        },
      };
    }
  ),

  updateTables: assign((context: LogicContext, event: UpdateTablesEvent) => {
    const { liveDealerTables } = event.data;

    return {
      ...context,
      currentTables: {
        ...context.currentTables,
        ...liveDealerTables,
      },
    };
  }),

  updatePersonalizedBanners: assign(
    (context: LogicContext, event: UpdatePersonalizedBannersEvent) => {
      const { banners } = event.payload;

      if (!banners) {
        return context;
      }

      let mapped = {};

      banners.forEach(b => {
        mapped = {
          ...mapped,
          [b.container]: b.rendered,
        };
      });

      return {
        ...context,
        currentPersonalizedBanners: mapped,
      };
    }
  ),

  initAuthTokenRefresh: send((context: LogicContext, event: any) => {
    // After the auth token expires. Query once for a refreshed token.
    // If the second answer is incorrect from the Data Gateway, clear the recently played section.
    if (
      context.user.refreshTokenRetryNumber &&
      context.user.refreshTokenRetryNumber > 1
    ) {
      return { type: 'CLEAR_RECENTLY_PLAYED' };
    }

    return { type: 'FETCH_REFRESHED_AUTH_TOKEN_WITH_DEPENDENCIES' };
  }),

  updateRetryStatus: assign((context: LogicContext) => {
    return {
      ...context,
      user: {
        ...context.user,
        refreshTokenRetryNumber:
          context.user.refreshTokenRetryNumber &&
          Number.isInteger(context.user.refreshTokenRetryNumber)
            ? ++context.user.refreshTokenRetryNumber
            : 1,
      },
    };
  }),

  clearRecentlyPlayedGames: assign((context: LogicContext) => {
    return {
      ...context,
      user: {
        ...context.user,
        recentlyPlayedGamesList: [],
      },
    };
  }),

  updateRecentlyPlayedGames: assign(
    (context: LogicContext, event: UpdateRecentlyPlayedEvent) => {
      const { recentlyPlayedGamesList } = event.data;

      return {
        ...context,
        user: {
          ...context.user,
          refreshTokenRetryNumber: 0,
          recentlyPlayedGamesList,
        },
      };
    }
  ),

  // PROMOTIONS
  setPromotionsListPageId: assign(() => {
    const promotionsListRoute = config.routes.find(
      r => r.subtype === 'promotions' && r.slug === ''
    );
    if (!promotionsListRoute) {
      throw new Error('could not find promotions list route');
    }
    return {
      currentPageId: promotionsListRoute.id,
    };
  }),

  showPromotionsList: (context: LogicContext) => {
    saveSiteSpecificTrackingInfo(
      context.trackingData,
      config.version
        ? context.currentPagePath.replace(`${config.version}`, '')
        : context.currentPagePath,
      context.segments
    );
    models.showPromotionsList(config.locale || config.defaultLocale);
  },

  showPromotionDetails: (context: LogicContext) => {
    saveSiteSpecificTrackingInfo(
      context.trackingData,
      config.version
        ? context.currentPagePath.replace(`${config.version}`, '')
        : context.currentPagePath,
      context.segments
    );
    const { components } = context.controllersData[controllersEnum.PROMOTIONS]!;
    const promotionDetails = components.find(
      ([id, entry]) => entry.properties.mode === 'details'
    )!;

    models.showPromotionsDetails(
      promotionDetails[1].properties.slug,
      config.locale || config.defaultLocale
    );
  },

  // GAME LAUNCH
  manageGameLaunchCallbackMode: assign((context: LogicContext) => {
    const { user, currentScreenSize } = context;
    const callbackMode = getCallbackMode(
      currentScreenSize,
      context.deviceInfo,
      user
    );

    return {
      ...context,
      callbackMode,
    };
  }),

  // SCREEN SIZE
  saveCurrentScreenSize: assign((context: LogicContext, event: any) => ({
    currentScreenSize: event.data.size,
  })),

  // AUTH TOKEN
  saveAuthToken: assign((context: LogicContext, event: AuthTokenEvent) => {
    if (event.data?.access_token) {
      return {
        ...context,
        user: {
          ...context.user,
          authToken: {
            ...event.data,
            initialSetup:
              context.user.authToken?.initialSetup &&
              context.user.authToken?.initialSetup === true
                ? false
                : true,
          },
        },
      };
    }

    return {
      ...context,
    };
  }),

  // REFRESHED AUTH TOKEN
  saveRefreshedAuthToken: assign(
    (context: LogicContext, event: AuthTokenEvent) => {
      if (event.data?.access_token) {
        return {
          ...context,
          user: {
            ...context.user,
            authToken: {
              ...context.user.authToken,
              ...event.data,
              initialSetup: false,
            },
          },
        };
      }
      return {
        ...context,
      };
    }
  ),

  fetchAuthToken: send('FETCH_AUTH_TOKEN'),

  fetchRefreshedSessionToken: send('FETCH_REFRESHED_AUTH_TOKEN'),

  checkSegmentsCookie: assign(
    (context: LogicContext, event: SegmentsEvent) => ({
      ...context,
      segments: event.data?.segments ? event.data?.segments : null,
    })
  ),

  saveSegments: assign((context: LogicContext, event: SegmentsEvent) => {
    const { data } = event;
    const segments =
      Array.isArray(data?.segments) && data.segments.length
        ? data.segments
        : [];

    try {
      Cookies.set(
        GAMING_SEGMENTS_COOKIE_NAME,
        JSON.stringify({ segments: segments }),
        {
          expires: 1, // 1 day
        }
      );
    } catch (e) {
      console.error('Cookie Segments could not be saved.');
    }

    return {
      ...context,
      segments,
    };
  }),

  fetchRecentlyPlayed: send('UPDATE_RECENTLY_PLAYED'),

  fetchRecentlyPlayedAfterDelay: send('UPDATE_RECENTLY_PLAYED_AFTER_DELAY'),

  // OPEN DEPOSIT
  openDeposit: () => {
    models.openDeposit();
  },

  // USER BALANCE
  hideBalance: () => {
    models.hideBalance();
  },

  showBalance: () => {
    models.showBalance();
  },

  /*------------------------SEND ACTIONS------------------------*/
  //GAME
  gameFetchFailed: send(
    (
      context: LogicContext,
      event: EventObject & { data: { path: string } }
    ) => {
      return {
        payload: event.data,
        type: 'UPDATE_PAGE',
      };
    }
  ),

  chooseGameModalCloseMethod: send((context: LogicContext) => {
    const shouldLauncherControlGameModalClose = config.launcher.controlGameModalClose?.includes(
      context.gameToLaunch?.integration as string
    );

    return {
      type: shouldLauncherControlGameModalClose
        ? 'VIEW_CLOSE_GAME_MODAL_THROUGH_GAME_FRAME'
        : 'VIEW_CLOSE_GAME_MODAL',
    };
  }),

  //ROUTING
  goBack: send('GLOBAL_GOBACK'),

  //NAVIGATION
  nav_HANDLE_CHANGE: send((context: LogicContext, event: any) => ({
    payload: event.payload,
    type: 'NAV_HANDLE_CHANGE',
  })),

  //LAYOUT CHANGE
  layout_openSystemErrorPage: send('LAYOUT_SYSTEM_ERROR_PAGE_OPEN'),
  layout_changePageId: send((context: LogicContext, event: any) => {
    const isInNav =
      context.navigationLinks &&
      context.navigationLinks.some(link => link === context.currentPagePath);

    return {
      type: 'LAYOUT_PAGEID_CHANGE',
      mainLayoutPath: isInNav
        ? context.currentPagePath
        : context.mainLayoutPath,
      reload_layout: !event.type || event.data?.loggedIn === false,
      event,
    };
  }),
  layout_reload: send((context: LogicContext, event: any) => ({
    type: 'LAYOUT_RELOAD',
    reload_layout: !event.type || event.data?.loggedIn === false,
    event,
  })),
  layout_notifyChangeEnd: send((context: LogicContext, event: any) => {
    return {
      type: 'LAYOUT_CHANGE_END',
      controllersData: event.data?.controllersData,
      mainLayoutPath: event.data?.mainLayoutPath,
      pageTitle: event.data?.pageTitle,
    };
  }),
  layout_recalculate: send((context: LogicContext, event: any) => ({
    type: 'LAYOUT_RECALCULATE',
    data: event.data,
  })),
  layout_closeSidebar: send((context: LogicContext, event: any) => {
    return {
      type: 'LAYOUT_MODAL_CLOSE',
      mainLayoutPath: event.data?.mainLayoutPath,
    };
  }),
  layout_closeGameModal: send('LAYOUT_GAME_MODAL_CLOSE'),
  layout_openGameModal: send('LAYOUT_GAME_MODAL_OPEN'),

  //OPEN MODAL
  gameModal_open: send((context: LogicContext, event: any) => ({
    type: 'OPEN_GAME_MODAL',
    payload: {
      path: context.gameToLaunch?.gameLaunchPath,
    },
  })),
  //OPEN GAME DETAILS
  gameDetails_open: send((context: LogicContext, event: any) => ({
    type: 'OPEN_GAME_DETAILS',
    payload: {
      path: context.gameToLaunch?.gameDetailsPath,
    },
  })),
  //LAUNCH GAME
  gameLaunch_trigger: send('LAUNCH_GAME'),
  //TRACKING
  tracking_changePage: send('TRACK_PAGE_CHANGE'),
  //RECENTLY PLAYED
  rp_rerender: send('RP_RERENDER'),
  //PROMOTIONS
  promotions_updateUrl: send(
    (
      context: LogicContext,
      event: EventObject & { payload?: { pageSlug?: string } }
    ) => ({
      type: 'UPDATE_PROMOTIONS_URL',
      pageSlug: event.payload && event.payload.pageSlug,
    })
  ),
  personalized_banners_refetch: send('REFETCH_PERSONALIZED_BANNERS'),
  //CATEGORIES
  categories_reload: send('RELOAD_CATEGORIES'),
  categories_refetch: send('REFETCH_CATEGORIES'),
};

export default actions;
